import { useCallback, useEffect, useMemo, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { useGitHubRepos } from './useGitHubRepos';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const REPO_TYPES = [
  { label: 'app', value: 'app' },
  { label: 'service', value: 'service' },
  { label: 'lib', value: 'lib' },
  { label: 'tool', value: 'tool' },
  { label: 'infra', value: 'infra' },
  { label: 'docs', value: 'docs' },
  { label: 'data', value: 'data' },
  { label: 'poc', value: 'poc' },
];

const REGIONAL_SUFFIXES = [
  { label: 'anz', value: 'anz' },
  { label: 'na', value: 'na' },
  { label: 'emea', value: 'emea' },
  { label: 'asia', value: 'asia' },
];

const SEGMENT_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

interface UiOptions {
  allowedHosts?: string[];
  allowedOwners?: string[];
  mode?: 'new' | 'existing';
  requestUserCredentials?: {
    secretsKey: string;
    additionalScopes?: { github?: string[] };
  };
}

function serializeRepoUrl(host: string, owner: string, repo: string): string {
  const params = new URLSearchParams();
  if (owner) params.set('owner', owner);
  if (repo) params.set('repo', repo);
  return `${host}?${params.toString()}`;
}

export const GitHubRepoPickerField = (
  props: FieldExtensionComponentProps<string, UiOptions>,
) => {
  const { onChange, rawErrors, required, uiSchema, formData, schema } = props;
  const uiOptions = (uiSchema?.['ui:options'] ?? {}) as UiOptions;
  const {
    allowedHosts = [],
    allowedOwners = [],
    mode = 'new',
    requestUserCredentials,
  } = uiOptions;

  const host = allowedHosts[0] ?? 'github.com';
  const owner = allowedOwners[0] ?? '';

  // Credential flow - same pattern as built-in RepoUrlPicker
  const scmAuthApi = useApi(scmAuthApiRef);
  const { secrets, setSecrets } = useTemplateSecrets();
  const [credentialsFetched, setCredentialsFetched] = useState(false);

  const accessToken = requestUserCredentials?.secretsKey
    ? secrets[requestUserCredentials.secretsKey]
    : undefined;

  useEffect(() => {
    if (!requestUserCredentials || credentialsFetched) return;

    scmAuthApi
      .getCredentials({
        url: `https://${host}`,
        additionalScope: {
          repoWrite: true,
          customScopes: requestUserCredentials.additionalScopes,
        },
      })
      .then(({ token }) => {
        setSecrets({ [requestUserCredentials.secretsKey]: token });
        setCredentialsFetched(true);
      })
      .catch(() => {
        // Token unavailable - existing mode will fall back to free-text
      });
  }, [
    scmAuthApi,
    setSecrets,
    requestUserCredentials,
    host,
    credentialsFetched,
  ]);

  if (mode === 'existing') {
    return (
      <ExistingRepoMode
        host={host}
        owner={owner}
        allowedOwners={allowedOwners}
        accessToken={accessToken}
        onChange={onChange}
        rawErrors={rawErrors}
        required={required}
        formData={formData}
        schema={schema}
      />
    );
  }

  return (
    <NewRepoMode
      host={host}
      owner={owner}
      onChange={onChange}
      rawErrors={rawErrors}
      required={required}
      formData={formData}
      schema={schema}
    />
  );
};

// ---------- New Repo Mode ----------

interface NewRepoModeProps {
  host: string;
  owner: string;
  onChange: (value: string | undefined) => void;
  rawErrors: string[];
  required?: boolean;
  formData?: string;
  schema: { title?: string; description?: string };
}

function NewRepoMode({
  host,
  owner,
  onChange,
  rawErrors,
  required,
  schema,
}: NewRepoModeProps) {
  const [repoType, setRepoType] = useState('service');
  const [productArea, setProductArea] = useState('');
  const [repoName, setRepoName] = useState('');
  const [hasRegional, setHasRegional] = useState(false);
  const [regionalSuffix, setRegionalSuffix] = useState('anz');

  const productAreaError = useMemo(() => {
    if (!productArea) return undefined;
    return SEGMENT_PATTERN.test(productArea)
      ? undefined
      : 'Must be lowercase kebab-case (e.g. "payments" or "user-auth")';
  }, [productArea]);

  const repoNameError = useMemo(() => {
    if (!repoName) return undefined;
    return SEGMENT_PATTERN.test(repoName)
      ? undefined
      : 'Must be lowercase kebab-case (e.g. "api" or "web-app")';
  }, [repoName]);

  const assembledName = useMemo(() => {
    const parts = [repoType, productArea, repoName].filter(Boolean);
    if (hasRegional && regionalSuffix) {
      parts.push(regionalSuffix);
    }
    return parts.join('-');
  }, [repoType, productArea, repoName, hasRegional, regionalSuffix]);

  const isComplete = repoType && productArea && repoName;
  const hasErrors = !!productAreaError || !!repoNameError;

  useEffect(() => {
    if (isComplete && !hasErrors) {
      onChange(serializeRepoUrl(host, owner, assembledName));
    } else {
      onChange(undefined);
    }
  }, [host, owner, assembledName, isComplete, hasErrors, onChange]);

  const hasFieldErrors = rawErrors?.length > 0;

  return (
    <Box>
      {schema.title && (
        <Typography variant="h6" gutterBottom>
          {schema.title}
        </Typography>
      )}

      <FormControl fullWidth margin="normal" required={required}>
        <TextField
          id="repo-type-select"
          select
          label="Type"
          value={repoType}
          onChange={e => setRepoType(e.target.value)}
          SelectProps={{ native: true }}
          variant="outlined"
          size="small"
          helperText="Repository type category"
        >
          {REPO_TYPES.map(t => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </TextField>
      </FormControl>

      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={!!productAreaError || (hasFieldErrors && !productArea)}
      >
        <TextField
          id="product-area-input"
          label="Product Area"
          value={productArea}
          onChange={e => setProductArea(e.target.value.toLowerCase())}
          variant="outlined"
          size="small"
          placeholder="e.g. payments, user-auth"
          error={!!productAreaError}
          helperText={productAreaError ?? 'Lowercase kebab-case product area'}
        />
      </FormControl>

      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={!!repoNameError || (hasFieldErrors && !repoName)}
      >
        <TextField
          id="repo-name-input"
          label="Name"
          value={repoName}
          onChange={e => setRepoName(e.target.value.toLowerCase())}
          variant="outlined"
          size="small"
          placeholder="e.g. api, web-app"
          error={!!repoNameError}
          helperText={repoNameError ?? 'Lowercase kebab-case name'}
        />
      </FormControl>

      <Box display="flex" alignItems="center" mt={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasRegional}
              onChange={e => setHasRegional(e.target.checked)}
              size="small"
            />
          }
          label="Add regional suffix"
        />
        {hasRegional && (
          <TextField
            select
            value={regionalSuffix}
            onChange={e => setRegionalSuffix(e.target.value)}
            SelectProps={{ native: true }}
            variant="outlined"
            size="small"
            style={{ minWidth: 120, marginLeft: 8 }}
          >
            {REGIONAL_SUFFIXES.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </TextField>
        )}
      </Box>

      {(productArea || repoName) && (
        <Box
          mt={2}
          p={1.5}
          bgcolor="action.hover"
          borderRadius={4}
          fontFamily="monospace"
        >
          <Typography variant="body2" color="textSecondary">
            Preview:
          </Typography>
          <Typography variant="body1">
            <strong>{assembledName || '...'}</strong>
          </Typography>
          {owner && (
            <Typography variant="caption" color="textSecondary">
              {owner}/{assembledName}
            </Typography>
          )}
        </Box>
      )}

      {hasFieldErrors && (
        <FormHelperText error>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
    </Box>
  );
}

// ---------- Existing Repo Mode ----------

interface ExistingRepoModeProps {
  host: string;
  owner: string;
  allowedOwners: string[];
  accessToken: string | undefined;
  onChange: (value: string | undefined) => void;
  rawErrors: string[];
  required?: boolean;
  formData?: string;
  schema: { title?: string; description?: string };
}

function ExistingRepoMode({
  host,
  owner,
  allowedOwners,
  accessToken,
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: ExistingRepoModeProps) {
  const { repos, loading, getReposForOwner } = useGitHubRepos(
    host,
    accessToken,
  );

  const selectedOwner = owner || allowedOwners[0] || '';

  const repoOptions = useMemo(() => {
    if (!selectedOwner) return repos.map(r => `${r.owner}/${r.name}`);
    return getReposForOwner(selectedOwner).map(r => r.name);
  }, [repos, selectedOwner, getReposForOwner]);

  // Parse current value to get selected repo
  const currentRepo = useMemo(() => {
    if (!formData) return '';
    try {
      const parsed = new URL(`https://${formData}`);
      return parsed.searchParams.get('repo') || '';
    } catch {
      return '';
    }
  }, [formData]);

  const handleRepoChange = useCallback(
    (_: unknown, newValue: string | null) => {
      if (newValue) {
        onChange(serializeRepoUrl(host, selectedOwner, newValue));
      } else {
        onChange(undefined);
      }
    },
    [host, selectedOwner, onChange],
  );

  const hasToken = !!accessToken;
  const hasFieldErrors = rawErrors?.length > 0;

  return (
    <Box>
      {schema.title && (
        <Typography variant="h6" gutterBottom>
          {schema.title}
        </Typography>
      )}

      {hasToken ? (
        <FormControl
          fullWidth
          margin="normal"
          required={required}
          error={hasFieldErrors && !currentRepo}
        >
          <Autocomplete
            value={currentRepo || null}
            onChange={handleRepoChange}
            options={repoOptions}
            loading={loading}
            renderInput={params => (
              <MuiTextField
                {...params}
                label="Repository"
                variant="outlined"
                size="small"
                required={required}
                error={hasFieldErrors && !currentRepo}
                helperText={
                  loading
                    ? 'Loading repositories...'
                    : 'Search for an existing repository'
                }
              />
            )}
            freeSolo
            autoSelect
          />
        </FormControl>
      ) : (
        <FormControl
          fullWidth
          margin="normal"
          required={required}
          error={hasFieldErrors && !currentRepo}
        >
          <TextField
            id="repo-name-freetext"
            label="Repository Name"
            value={currentRepo}
            onChange={e => {
              const repo = e.target.value;
              if (repo) {
                onChange(serializeRepoUrl(host, selectedOwner, repo));
              } else {
                onChange(undefined);
              }
            }}
            variant="outlined"
            size="small"
            placeholder="Enter repository name"
            helperText="GitHub token unavailable - enter repository name manually"
          />
        </FormControl>
      )}

      {hasFieldErrors && (
        <FormHelperText error>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
    </Box>
  );
}
